import {t} from '@lingui/macro'
import {useState} from 'react'

import {BillingInterval, Interval, PlanTypeName} from '@product-web/api-types/billing'
import {PlanType} from '@product-web/api-types/plan'
import type {Subscription} from '@product-web/bff-moons/generated/beyond'
import {useFlags} from '@product-web/flags'
import type Country from '@product-web/locale/country'
import {GridItem} from '@product-web/telescope-lab/grid'
import {useUser} from '@product-web/user'

import {getAdvancedPlanBenefits, getEssentialPlanBenefits, getStarterPlanBenefits} from './helpers'
import {
    BoxedItem,
    BoxedItemHighlighted,
    StretchGrid,
    StretchGridItem,
} from './modal-choose-plans.styles'
import ConfirmChoosePlanModal from './modal-confirm-choose-plan'
import PlanOption from './plan-option'

import type {PlansInfo} from '../index.bff'

export interface PlanPrices {
    currency: string
    price: number
    discountedPrice: number
}

const PlanOptions = ({
    starterPlanUserLimit,
    subscription,
    updatePlan,
    planPrices,
    isOwner,
    country,
    onDismiss,
    commitmentInterval,
    billingInterval,
}: {
    starterPlanUserLimit: number
    subscription: Subscription | null
    updatePlan: (
        planType: PlanType,
        interval: Interval.MONTH | Interval.YEAR,
        planPrices?: {
            essentialPlanPrices?: PlanPrices
            advancedPlanPrices?: PlanPrices
        },
    ) => void
    planPrices?: PlansInfo['plansPrices']
    isOwner: boolean
    country: Country
    onDismiss: Function
    commitmentInterval: Interval.MONTH | Interval.YEAR
    billingInterval: BillingInterval
}) => {
    const user = useUser()
    const [selectedPlan, setSelectedPlan] = useState<PlanType | undefined>()

    const {pricing3CopyExperiment} = useFlags()

    let essentialPlanPrices = {
        currency: planPrices?.ESSENTIAL.currency!,
        price: planPrices?.ESSENTIAL.prices.monthly!,
        discountedPrice: planPrices?.ESSENTIAL.prices.monthlyDiscounted!,
    }

    let advancedPlanPrices = {
        currency: planPrices?.ADVANCED.currency!,
        price: planPrices?.ADVANCED.prices.monthly!,
        discountedPrice: planPrices?.ADVANCED.prices.monthlyDiscounted!,
    }

    if (billingInterval === BillingInterval.YEARLY) {
        essentialPlanPrices = {
            currency: planPrices?.ESSENTIAL.currency!,
            price: planPrices?.ESSENTIAL.prices.yearly!,
            discountedPrice: planPrices?.ESSENTIAL.prices.yearlyDiscounted!,
        }

        advancedPlanPrices = {
            currency: planPrices?.ADVANCED.currency!,
            price: planPrices?.ADVANCED.prices.yearly!,
            discountedPrice: planPrices?.ADVANCED.prices.yearlyDiscounted!,
        }
    }

    const intervalType = billingInterval === BillingInterval.YEARLY ? Interval.YEAR : Interval.MONTH

    const starterPlanUserLimitExp = starterPlanUserLimit - 1

    return (
        <>
            <ConfirmChoosePlanModal
                isOpen={Boolean(selectedPlan)}
                planType={selectedPlan as PlanType}
                commitmentInterval={commitmentInterval}
                billingInterval={billingInterval}
                onConfirm={() => {
                    if (selectedPlan) {
                        updatePlan(selectedPlan, intervalType, {
                            essentialPlanPrices,
                            advancedPlanPrices,
                        })
                        setSelectedPlan(undefined)
                    }
                }}
                onDismiss={() => {
                    setSelectedPlan(undefined)
                }}
            />
            <StretchGrid margin={10}>
                <StretchGridItem auto width={322}>
                    <BoxedItem>
                        <PlanOption
                            benefits={getStarterPlanBenefits(country)}
                            planHeader={PlanTypeName[PlanType.STARTER]}
                            prices={planPrices?.STARTER.prices}
                            planCurrency={planPrices?.STARTER.currency}
                            planType={PlanType.STARTER}
                            subscription={subscription}
                            header={t`All you need to get started:`}
                            buttonText={t`Continue with Starter`}
                            buttonOnClick={() => {
                                updatePlan(PlanType.STARTER, Interval.MONTH, {
                                    essentialPlanPrices,
                                    advancedPlanPrices,
                                })
                            }}
                            buttonVariant="secondary"
                            isEligible={(user?.company?.numberOfUsers || 0) <= starterPlanUserLimit}
                            onDismiss={onDismiss}
                            isOwner={isOwner}
                            startPlanUserLimit={starterPlanUserLimit}
                            planSubtitleText={
                                pricing3CopyExperiment
                                    ? t`${starterPlanUserLimitExp} users included.`
                                    : t`Up to ${starterPlanUserLimit} users`
                            }
                            commitmentInterval={commitmentInterval}
                            billingInterval={billingInterval}
                        />
                    </BoxedItem>
                </StretchGridItem>
                <GridItem auto width={322}>
                    <BoxedItemHighlighted>
                        <PlanOption
                            benefits={getEssentialPlanBenefits(country)}
                            planHeader={PlanTypeName[PlanType.ESSENTIAL]}
                            prices={planPrices?.ESSENTIAL.prices}
                            planType={PlanType.ESSENTIAL}
                            subscription={subscription}
                            header={t`Everything in Starter, plus:`}
                            buttonText={t`Continue with Essential`}
                            buttonOnClick={() => {
                                setSelectedPlan(PlanType.ESSENTIAL)
                            }}
                            buttonVariant="primary"
                            isEligible
                            onDismiss={onDismiss}
                            isOwner={isOwner}
                            startPlanUserLimit={starterPlanUserLimit}
                            planSubtitleText={t`${starterPlanUserLimit} users included.`}
                            recommended
                            commitmentInterval={commitmentInterval}
                            billingInterval={billingInterval}
                            planCurrency={planPrices?.ESSENTIAL.currency}
                            country={country}
                        />
                    </BoxedItemHighlighted>
                </GridItem>
                <StretchGridItem auto width={322}>
                    <BoxedItem>
                        <PlanOption
                            benefits={getAdvancedPlanBenefits(country)}
                            planHeader={PlanTypeName[PlanType.ADVANCED]}
                            prices={planPrices?.ADVANCED.prices}
                            planType={PlanType.ADVANCED}
                            subscription={subscription}
                            header={t`Everything in Essential, plus:`}
                            buttonText={t`Continue with Advanced`}
                            buttonOnClick={() => {
                                setSelectedPlan(PlanType.ADVANCED)
                            }}
                            buttonVariant="secondary"
                            isEligible
                            onDismiss={onDismiss}
                            isOwner={isOwner}
                            startPlanUserLimit={starterPlanUserLimit}
                            planSubtitleText={t`${starterPlanUserLimit} users included.`}
                            commitmentInterval={commitmentInterval}
                            billingInterval={billingInterval}
                            planCurrency={planPrices?.ADVANCED.currency}
                            country={country}
                        />
                    </BoxedItem>
                </StretchGridItem>
            </StretchGrid>
        </>
    )
}

export default PlanOptions
